import { keepPreviousData } from '@tanstack/react-query';
import { Navigate, createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

import { parseApiError } from '@/api/api-axios';
import { useGetEstateBranchesQuery } from '@/api/api-hooks';
import { RequireFeatureFlag } from '@/components/Auth/RequireFeatureFlag';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { Pagination } from '@/components/BaseTable/Pagination';
import { BranchCard } from '@/components/Branches/BranchCard';
import { BranchListSection } from '@/components/Branches/BranchListSection';
import { BranchListSkeleton } from '@/components/Branches/BranchListSkeleton';
import { BranchStatusFilter } from '@/components/Branches/BranchStatusFilter';
import { BranchWindowToggle } from '@/components/Branches/BranchWindowToggle';
import { CoverageBanner } from '@/components/Branches/CoverageBanner';
import { SearchFilter } from '@/components/Filters/SearchFilter';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useBranchPaginationTable } from '@/hooks/useBranchPaginationTable';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { useDebouncedSearchValue } from '@/hooks/useDebouncedSearchValue';
import { AppLink } from '@/types/navigation';
import { codebaseSettingsLinkOptions } from '@/utils/codebaseUrl';
import { toEstateBranchesParams } from '@/utils/estate';
import { codebaseSplatRouteApi } from '@/utils/route';
import { searchParam } from '@/utils/router';

// Mirrors `_dashboard/branches.tsx`'s `branchesSearchSchema`, minus the `codebase` filter field:
// this surface is scoped to a single codebase, so the codebase is fixed (no codebase filter
// control, no "by codebase" group tab). `group` is pinned to `'branch'` at the call site for a
// flat branch-card list, so it is omitted from the URL schema here.
const branchesSearchSchema = z.object({
  window: z.enum(['last_7d', 'last_30d', 'last_90d', 'all']).default('last_30d').catch('last_30d'),
  // 'merged' is intentionally omitted (merged lifecycle is gated off in v1) so a stale/bookmarked
  // ?status=merged URL is coerced away by the fallback.
  status: z
    .array(z.enum(['active', 'stale']))
    .default([])
    .catch([]),
  search: searchParam().optional(),
  sortBy: z.enum(['last_commit_date', 'name', 'unique_commits', 'current_sloc']).catch('last_commit_date').optional(),
  sortDirection: z.enum(['ASC', 'DESC']).catch('DESC').optional(),
  pageIndex: z.number().default(0).catch(0),
  pageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  branch: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/branches')({
  component: GatedCodebaseBranchesPage,
  validateSearch: branchesSearchSchema,
});

// Gate the per-codebase branches surface behind the BRANCHES_UI flag. When the flag is
// loaded-and-off, redirect to this codebase's settings; while loading, show the branch-list
// skeleton (avoids flashing a redirect before /me resolves). See RequireFeatureFlag.
function GatedCodebaseBranchesPage() {
  const { organizationName, codebaseId } = useCodebaseRoute();
  const search = codebaseSplatRouteApi.useSearch();
  return (
    <RequireFeatureFlag
      flag={FEATURE_FLAGS.BRANCHES_UI}
      loading={<BranchListSkeleton />}
      redirect={<Navigate replace {...codebaseSettingsLinkOptions({ codebaseId, organizationName }, search)} />}
    >
      <CodebaseBranchesPage />
    </RequireFeatureFlag>
  );
}

const routeApi = getRouteApi('/$organizationName/cb/$codebaseId/branches');

function CodebaseBranchesPage() {
  const { codebaseId, organizationName } = useCodebaseRoute();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const { searchValue, setSearchValue } = useDebouncedSearchValue<typeof search>({
    searchQuery: search.search,
    navigate,
    searchParamName: 'search',
    resetPageIndexParamName: 'pageIndex',
  });

  // All filtering / sorting / pagination is server-side: the URL search params are mapped to
  // request params and sent to the estate endpoint, with `codebase_id` pinned to this codebase via
  // a call-site spread override (the pure `toEstateBranchesParams` util is unchanged). `group` is
  // pinned to `'branch'` for the flat branch-card list. The returned `items` are rendered as-is.
  const params = useMemo(
    () => ({ ...toEstateBranchesParams({ ...search, group: 'branch' }), codebase_id: [codebaseId] }),
    [search, codebaseId]
  );
  // `keepPreviousData` keeps the last successful page on screen while a new param set is fetching,
  // so changing filters/range doesn't blank the page to skeletons. Set in the hook layer (Plan 03
  // flicker fix).
  const {
    data: estateBranchesData,
    error,
    isLoading,
    isError,
  } = useGetEstateBranchesQuery(params, {
    placeholderData: keepPreviousData,
  });

  const branches = useMemo(() => estateBranchesData?.items ?? [], [estateBranchesData]);
  const summary = estateBranchesData?.summary;
  const totalCount = estateBranchesData?.total_count ?? 0;

  const paginationTable = useBranchPaginationTable({
    branches,
    totalCount,
    pagination: { pageIndex: search.pageIndex, pageSize: search.pageSize },
    onPaginationChange: ({ pageIndex, pageSize }) => {
      navigate({ search: (prev) => ({ ...prev, pageIndex, pageSize }) });
    },
  });

  const setSearchParams = (updates: Partial<typeof search>) => {
    navigate({ search: (prev) => ({ ...prev, pageIndex: 0, ...updates }) });
  };

  const toggleStatus = (value: 'active' | 'merged' | 'stale') => {
    // 'merged' is gated out of the filter UI and coerced out of the URL, so only active/stale reach
    // here; the early return also narrows the type to the route's status enum.
    if (value === 'merged') return;
    const next = (search.status ?? []).includes(value)
      ? (search.status ?? []).filter((s) => s !== value)
      : [...(search.status ?? []), value];
    setSearchParams({ status: next });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <BranchStatusFilter
            status={search.status ?? []}
            summary={summary}
            onClearStatus={() => setSearchParams({ status: [] })}
            onToggle={toggleStatus}
          />

          <BranchWindowToggle value={search.window} onValueChange={(window) => setSearchParams({ window })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <SearchFilter
            name="codebase-branch-search"
            placeholder="Search branches…"
            value={searchValue ?? ''}
            onChange={setSearchValue}
            className="w-72"
          />
        </div>
      </div>

      {/* The CoverageBanner is persistent chrome — it always renders (fed by the latest or previous
          `summary`) and is never swapped out by the loading/error state. Only the branch-list
          region below reacts to loading / error / empty. */}
      <BranchListSection>
        <CoverageBanner
          bare
          activeCount={summary?.active ?? 0}
          mergedCount={summary?.merged ?? 0}
          staleCount={summary?.stale ?? 0}
          generatedCount={summary?.with_context ?? 0}
          totalCount={summary?.total ?? 0}
          commitCount={summary?.total_commits}
          isLoading={isLoading}
        />

        {isError && !estateBranchesData ? (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load branch activity</AlertTitle>
              <AlertDescription>
                {parseApiError(error, 'Something went wrong fetching branches for this codebase. Try again.').message}
              </AlertDescription>
            </Alert>
          </div>
        ) : isLoading ? (
          <BranchListSkeleton />
        ) : branches.length === 0 ? (
          <p className="text-muted-foreground p-4 text-sm">No branches found for this codebase.</p>
        ) : (
          <>
            <div className="flex flex-col gap-2 p-4">
              {branches.map((branch) => {
                const link: AppLink = {
                  to: '/$organizationName/cb/$codebaseId/branches',
                  params: { organizationName, codebaseId },
                  search: (prev) => ({ ...prev, pageIndex: 0, branch: branch.name }),
                };

                return <BranchCard key={`${branch.codebase_id}/${branch.name}`} branch={branch} link={link} />;
              })}
            </div>
            <div className="px-4 pb-4">
              <Pagination table={paginationTable} total={totalCount} />
            </div>
          </>
        )}
      </BranchListSection>
    </div>
  );
}
